import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPermission } from '../utils/permissionUtils';
import '../styles/manage-issues.css';

const ManageIssues = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    priority: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Check permissions
  const canManageIssues = hasPermission(user, 'manage-issues');

  useEffect(() => {
    if (isOpen && canManageIssues) {
      loadIssues();
    }
  }, [isOpen, canManageIssues, filters, pagination.page]);

  const loadIssues = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      });

      // Add filters if they exist
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await fetch(`${API_BASE}/api/issues?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setIssues(data.issues);
        setPagination(prev => ({
          ...prev,
          total: data.pagination.total,
          totalPages: data.pagination.totalPages
        }));
      } else {
        throw new Error(t('issues.errors.failedToLoad'));
      }
    } catch (error) {
      console.error('Error loading issues:', error);
      setError(t('issues.errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const updateIssueStatus = async (issueId, newStatus) => {
    setUpdating(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/issues?id=${issueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        // Refresh the issues list
        await loadIssues();
        if (selectedIssue && selectedIssue.id === issueId) {
          const updatedIssue = await response.json();
          setSelectedIssue(updatedIssue);
        }
      } else {
        throw new Error(t('issues.errors.failedToUpdateStatus'));
      }
    } catch (error) {
      console.error('Error updating issue:', error);
      setError(t('issues.errors.failedToUpdateStatus'));
    } finally {
      setUpdating(false);
    }
  };

  const assignIssue = async (issueId, assignedTo) => {
    setUpdating(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/issues?id=${issueId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assigned_to: assignedTo }),
      });

      if (response.ok) {
        await loadIssues();
        if (selectedIssue && selectedIssue.id === issueId) {
          const updatedIssue = await response.json();
          setSelectedIssue(updatedIssue);
        }
      } else {
        throw new Error(t('issues.errors.failedToAssign'));
      }
    } catch (error) {
      console.error('Error assigning issue:', error);
      setError(t('issues.errors.failedToAssign'));
    } finally {
      setUpdating(false);
    }
  };

  const deleteIssue = async (issueId) => {
    if (!window.confirm(t('issues.confirmations.deleteIssue'))) return;
    
    setUpdating(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/issues?id=${issueId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await loadIssues();
        if (selectedIssue && selectedIssue.id === issueId) {
          setSelectedIssue(null);
        }
      } else {
        throw new Error(t('issues.errors.failedToDelete'));
      }
    } catch (error) {
      console.error('Error deleting issue:', error);
      setError(t('issues.errors.failedToDelete'));
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      'open': '#ef4444',
      'in-progress': '#f59e0b',
      'resolved': '#10b981',
      'closed': '#6b7280',
      'duplicate': '#8b5cf6'
    };
    return colors[status] || '#6b7280';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': '#10b981',
      'medium': '#f59e0b',
      'high': '#ef4444',
      'critical': '#dc2626'
    };
    return colors[priority] || '#6b7280';
  };

  const getTypeIcon = (type) => {
    const icons = {
      'bug': '🐛',
      'suggestion': '💡',
      'feature-request': '✨'
    };
    return icons[type] || '📝';
  };

  const getStatusTranslationKey = (status) => {
    const statusMap = {
      'open': 'open',
      'in-progress': 'inProgress',
      'resolved': 'resolved',
      'closed': 'closed',
      'duplicate': 'duplicate'
    };
    return statusMap[status] || status;
  };

  if (!isOpen) return null;

  if (!canManageIssues) {
    return (
      <div className="manage-issues-overlay">
        <div className="manage-issues-dialog">
          <div className="manage-issues-header">
            <h2>{t('issues.accessDenied')}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="manage-issues-content">
            <p>{t('issues.noPermission')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-issues-overlay">
      <div className="manage-issues-dialog">
        <div className="manage-issues-header">
          <div className="header-content">
            <div className="header-icon">🐛</div>
            <div className="header-text">
              <h2>{t('issues.title')}</h2>
              <p>{t('issues.description')}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="manage-issues-content">
          {/* Filters */}
          <div className="filters-section">
            <div className="filter-group">
              <label>{t('issues.status')}</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">{t('issues.statuses.all')}</option>
                <option value="open">{t('issues.statuses.open')}</option>
                <option value="in-progress">{t('issues.statuses.inProgress')}</option>
                <option value="resolved">{t('issues.statuses.resolved')}</option>
                <option value="closed">{t('issues.statuses.closed')}</option>
                <option value="duplicate">{t('issues.statuses.duplicate')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('issues.type')}</label>
              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              >
                <option value="">{t('issues.statuses.all')}</option>
                <option value="bug">{t('issues.types.bug')}</option>
                <option value="suggestion">{t('issues.types.suggestion')}</option>
                <option value="feature-request">{t('issues.types.feature')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('issues.priority')}</label>
              <select
                value={filters.priority}
                onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              >
                <option value="">{t('issues.statuses.all')}</option>
                <option value="low">{t('issues.priorities.low')}</option>
                <option value="medium">{t('issues.priorities.medium')}</option>
                <option value="high">{t('issues.priorities.high')}</option>
                <option value="critical">{t('issues.priorities.critical')}</option>
              </select>
            </div>

            <div className="filter-group">
              <label>{t('issues.sort')}</label>
              <select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split('-');
                  setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                }}
              >
                <option value="created_at-DESC">{t('issues.sortOptions.newest')}</option>
                <option value="created_at-ASC">{t('issues.sortOptions.oldest')}</option>
                <option value="updated_at-DESC">{t('issues.sortOptions.recentlyUpdated')}</option>
                <option value="priority-DESC">{t('issues.sortOptions.priority')}</option>
                <option value="title-ASC">{t('issues.sortOptions.alphabetical')}</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              {error}
            </div>
          )}

          <div className="issues-layout">
            {/* Issues List */}
            <div className="issues-list">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>{t('issues.loading')}</p>
                </div>
              ) : issues.length === 0 ? (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,12H10V10H14M14,16H10V14H14M20,8L14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <p>{t('issues.noIssues')}</p>
                </div>
              ) : (
                <>
                  {issues.map((issue) => (
                    <div
                      key={issue.id}
                      className={`issue-item ${selectedIssue?.id === issue.id ? 'selected' : ''}`}
                      onClick={() => setSelectedIssue(issue)}
                    >
                      <div className="issue-header">
                        <div className="issue-title">
                          <span className="issue-type">{getTypeIcon(issue.type)}</span>
                          {issue.title}
                        </div>
                        <div className="issue-meta">
                          <span 
                            className="status-badge" 
                            style={{ backgroundColor: getStatusColor(issue.status) }}
                          >
                            {t(`issues.statuses.${getStatusTranslationKey(issue.status)}`)}
                          </span>
                          <span 
                            className="priority-badge"
                            style={{ color: getPriorityColor(issue.priority) }}
                          >
                            {t(`issues.priorities.${issue.priority}`)}
                          </span>
                        </div>
                      </div>
                      <div className="issue-summary">
                        <p>{issue.description.substring(0, 100)}...</p>
                        <div className="issue-info">
                          <span>{t('issues.details.byReporter', { reporter: issue.reporter_name || issue.reporter_email })}</span>
                          <span>{formatDate(issue.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Pagination */}
                  {pagination.totalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                      >
                        {t('issues.pagination.previous')}
                      </button>
                      <span>
                        {t('issues.pagination.pageOf', { page: pagination.page, total: pagination.totalPages })}
                      </span>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}
                      >
                        {t('issues.pagination.next')}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Issue Details */}
            {selectedIssue && (
              <div className="issue-details">
                <div className="issue-details-header">
                  <h3>
                    <span className="issue-type">{getTypeIcon(selectedIssue.type)}</span>
                    {selectedIssue.title}
                  </h3>
                  <div className="issue-actions">
                    <select
                      value={selectedIssue.status}
                      onChange={(e) => updateIssueStatus(selectedIssue.id, e.target.value)}
                      disabled={updating}
                    >
                      <option value="open">{t('issues.statusOptions.open')}</option>
                      <option value="in-progress">{t('issues.statusOptions.inProgress')}</option>
                      <option value="resolved">{t('issues.statusOptions.resolved')}</option>
                      <option value="closed">{t('issues.statusOptions.closed')}</option>
                      <option value="duplicate">{t('issues.statusOptions.duplicate')}</option>
                    </select>
                    <button
                      className="delete-button"
                      onClick={() => deleteIssue(selectedIssue.id)}
                      disabled={updating}
                    >
                      {t('issues.details.deleteButton')}
                    </button>
                  </div>
                </div>

                <div className="issue-details-content">
                  <div className="detail-section">
                    <h4>{t('issues.details.description')}</h4>
                    <div className="description-content" dangerouslySetInnerHTML={{ __html: selectedIssue.description }} />
                  </div>

                  {selectedIssue.type === 'bug' && (
                    <>
                      {selectedIssue.steps_to_reproduce && (
                        <div className="detail-section">
                          <h4>{t('issues.details.stepsToReproduce')}</h4>
                          <pre>{selectedIssue.steps_to_reproduce}</pre>
                        </div>
                      )}

                      <div className="behavior-row">
                        {selectedIssue.expected_behavior && (
                          <div className="detail-section">
                            <h4>{t('issues.details.expectedBehavior')}</h4>
                            <p>{selectedIssue.expected_behavior}</p>
                          </div>
                        )}

                        {selectedIssue.actual_behavior && (
                          <div className="detail-section">
                            <h4>{t('issues.details.actualBehavior')}</h4>
                            <p>{selectedIssue.actual_behavior}</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {selectedIssue.attachments?.length > 0 && (
                    <div className="detail-section">
                      <h4>{t('issues.details.attachments')}</h4>
                      <div className="attachments-grid">
                        {selectedIssue.attachments.map((attachment, index) => (
                          <a
                            key={index}
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="attachment-link"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                            </svg>
                            {attachment.originalName}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="detail-section">
                    <h4>{t('issues.details.technicalInfo')}</h4>
                    <div className="tech-info">
                      <div className="info-item">
                        <label>{t('issues.details.reporter')}</label>
                        <span>{selectedIssue.reporter_name || selectedIssue.reporter_email}</span>
                      </div>
                      <div className="info-item">
                        <label>{t('issues.details.created')}</label>
                        <span>{formatDate(selectedIssue.created_at)}</span>
                      </div>
                      <div className="info-item">
                        <label>{t('issues.details.updated')}</label>
                        <span>{formatDate(selectedIssue.updated_at)}</span>
                      </div>
                      {selectedIssue.browser_info?.userAgent && (
                        <div className="info-item">
                          <label>{t('issues.details.browser')}</label>
                          <span className="user-agent">{selectedIssue.browser_info.userAgent}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageIssues;